'use client';

import { useEffect, useRef, useState } from 'react';
import { useTranslations, useLocale } from 'next-intl';
import type { User } from '@supabase/supabase-js';
import { buttonClass } from '@optego/ui';
import { Link, usePathname, useRouter } from '@/i18n/navigation';
import { createClient } from '@/lib/supabase/client';
import type { Locale } from '@/i18n/config';
import Logo from '@/components/Logo';

type Domain = { slug: string; name: string };

export default function Header({ domains }: { domains: Domain[] }) {
  const t = useTranslations('common');
  const locale = useLocale() as Locale;
  const pathname = usePathname();
  const router = useRouter();
  const [user, setUser] = useState<User | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const supabase = createClient();

    supabase.auth.getUser().then(({ data }) => {
      setUser(data.user);
    });

    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });

    return () => subscription.unsubscribe();
  }, []);

  // Escape closes the drawer and the account menu; a click outside closes the account menu
  useEffect(() => {
    if (!menuOpen && !drawerOpen) return;

    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') {
        setMenuOpen(false);
        setDrawerOpen(false);
      }
    }
    function onPointerDown(event: PointerEvent) {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) setMenuOpen(false);
    }

    document.addEventListener('keydown', onKeyDown);
    document.addEventListener('pointerdown', onPointerDown);
    return () => {
      document.removeEventListener('keydown', onKeyDown);
      document.removeEventListener('pointerdown', onPointerDown);
    };
  }, [menuOpen, drawerOpen]);

  function closeMenus() {
    setMenuOpen(false);
    setDrawerOpen(false);
  }

  function switchLocale() {
    closeMenus();
    router.replace(pathname, { locale: locale === 'hu' ? 'en' : 'hu' });
  }

  async function handleLogout() {
    const supabase = createClient();
    await supabase.auth.signOut();
    setUser(null);
    closeMenus();
    router.push('/');
  }

  const displayName = user?.user_metadata?.full_name || user?.email?.split('@')[0] || '';
  const catalogActive = pathname.startsWith('/products') || pathname.startsWith('/collections');

  return (
    <header className="site-header">
      <div className="wrap site-header__inner">
        <Link href="/" className="site-header__logo" aria-label={t('homeLink')} onClick={closeMenus}>
          <Logo />
        </Link>

        <nav className="site-nav" aria-label={t('mainNav')}>
          <ul className="site-nav__list">
            <li>
              <Link href="/products" aria-current={catalogActive ? 'page' : undefined}>
                {t('products')}
              </Link>
            </li>
            {/* Domains come from the categories table */}
            {domains.map((domain) => (
              <li key={domain.slug}>
                <Link href={{ pathname: '/products', query: { category: domain.slug } }}>{domain.name}</Link>
              </li>
            ))}
          </ul>

          <div className="site-nav__actions">
            <button type="button" className="lang-toggle" onClick={switchLocale} aria-label={t('switchLanguage')}>
              {locale === 'hu' ? 'EN' : 'HU'}
            </button>

            {user ? (
              <div className="user-menu" ref={menuRef}>
                <button
                  type="button"
                  className="user-menu__button"
                  aria-expanded={menuOpen}
                  aria-controls="user-menu-panel"
                  onClick={() => setMenuOpen((open) => !open)}
                >
                  <span className="user-menu__avatar" aria-hidden="true">
                    {displayName.charAt(0).toUpperCase()}
                  </span>
                  <span className="user-menu__name">{displayName}</span>
                </button>
                {menuOpen && (
                  <div id="user-menu-panel" className="user-menu__panel">
                    <Link href="/dashboard" className="user-menu__item" onClick={closeMenus}>
                      {t('dashboard')}
                    </Link>
                    <Link href="/dashboard/settings" className="user-menu__item" onClick={closeMenus}>
                      {t('settings')}
                    </Link>
                    <button type="button" className="user-menu__item user-menu__item--danger" onClick={handleLogout}>
                      {t('logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link href="/auth" className="site-nav__login">
                  {t('login')}
                </Link>
                <Link href={{ pathname: '/auth', query: { mode: 'register' } }} className={buttonClass({ size: 'sm' }, 'site-nav__cta')}>
                  {t('register')}
                </Link>
              </>
            )}

            <button
              type="button"
              className="nav-toggle"
              aria-expanded={drawerOpen}
              aria-controls="mobile-drawer"
              aria-label={drawerOpen ? t('closeMenu') : t('openMenu')}
              onClick={() => setDrawerOpen((open) => !open)}
            >
              <span className="nav-toggle__icon" aria-hidden="true" />
            </button>
          </div>
        </nav>
      </div>

      <div id="mobile-drawer" className="mobile-drawer" data-open={drawerOpen}>
        <div className="wrap">
          <ul className="mobile-drawer__list">
            <li>
              <Link href="/products" onClick={closeMenus}>
                {t('products')}
              </Link>
            </li>
            {domains.map((domain) => (
              <li key={domain.slug}>
                <Link href={{ pathname: '/products', query: { category: domain.slug } }} onClick={closeMenus}>
                  {domain.name}
                </Link>
              </li>
            ))}
            {user && (
              <>
                <li>
                  <Link href="/dashboard" onClick={closeMenus}>
                    {t('dashboard')}
                  </Link>
                </li>
                <li>
                  <Link href="/dashboard/settings" onClick={closeMenus}>
                    {t('settings')}
                  </Link>
                </li>
              </>
            )}
          </ul>

          <div className="flex flex-col gap-3">
            {user ? (
              <button type="button" className={buttonClass({ variant: 'ghost', size: 'lg', block: true })} onClick={handleLogout}>
                {t('logout')}
              </button>
            ) : (
              <>
                <Link href={{ pathname: '/auth', query: { mode: 'register' } }} className={buttonClass({ size: 'lg', block: true })} onClick={closeMenus}>
                  {t('register')}
                </Link>
                <Link href="/auth" className={buttonClass({ variant: 'ghost', size: 'lg', block: true })} onClick={closeMenus}>
                  {t('login')}
                </Link>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  );
}
